import React from 'react';
import {
    Alert,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Typography,
} from '@mui/material';
import { formatFloat } from '../../../../../helpers';
import { MachineCandidate } from './plan-allocation';

interface IMachinePickerDialog {
    // null closes the dialog.
    productId: number | null;
    productDescription: string;
    // Candidate rates are stored in kg/hr for allocation. Products with a
    // group weight can display the same rate in bultos/h for the planner.
    groupWeight: number | null | undefined;
    candidates: MachineCandidate[];
    onPick: (rowIndex: number) => void;
    onClose: () => void;
}

// Why a machine cannot be picked, in the order the planner should read it.
// Written out rather than left implicit: nobody is using this page yet, and a
// disabled row whose reason is missing is the fastest way to make them go back
// to WhatsApp.
const reasonFor = (candidate: MachineCandidate): string | null => {
    if (!candidate.compatible) return 'otro tipo de producción';
    if (!candidate.hasFreeHours) return 'sin horas libres';
    if (candidate.rateStatus === 'pending') return 'calculando rendimiento…';
    if (candidate.rateStatus === 'none') {
        // 'none' means NEITHER the product's own runs NOR the machine's average
        // could produce a rate, so blaming the product would understate it —
        // there is nothing to compute from on this machine at all.
        return 'sin rendimiento calculable en esta máquina — agrégalo desde el renglón';
    }
    return null;
};

// Which machines have run this product before is carried by the LIST ORDER and
// two section headers, not by a badge on every row — the same shape as the row's
// product picker, and for the same reason: a chip repeated down twenty rows is
// noise, while a heading is read once.
//
// Both labels name the 12-month window on purpose, because the query behind them
// is deliberately bounded to it — a bare "nunca" would be false for a product the
// machine ran two years ago.
const PRODUCED_HEADER = 'Ya lo han producido el último año';
const NEVER_PRODUCED_HEADER = 'Sin producción de este producto el último año';

// The header to print above a candidate, or null when it continues the section
// above it. Nothing is grouped until the histories have landed: with every
// `producedHere` still undefined, filing the whole list under "sin producción"
// would be a claim about every machine that cannot be made while the queries are
// in flight — so the list stays flat and alphabetical for that moment.
const headerFor = (
    candidate: MachineCandidate,
    previous: MachineCandidate | undefined,
): string | null => {
    if (candidate.producedHere === undefined) return null;
    const header = candidate.producedHere
        ? PRODUCED_HEADER
        : NEVER_PRODUCED_HEADER;
    if (previous === undefined) return header;
    const previousHeader =
        previous.producedHere === undefined
            ? null
            : previous.producedHere
            ? PRODUCED_HEADER
            : NEVER_PRODUCED_HEADER;
    return header === previousHeader ? null : header;
};

// Which machine should run a product picked from the pedidos drawer.
//
// EVERY row in the plan is listed, not only the ones that can take the product.
// A list that silently omits a machine cannot answer the question the planner is
// actually asking — "why isn't M7 an option?" — so incompatible and full rows
// stay visible, disabled, each carrying its own reason.
//
// The list is in two alphabetical sections — machines that have run this product
// before, then the rest — rather than ranked best-first. A ranked list moved
// under the cursor as rates landed, and it made a machine hard to find by name,
// which is how a planner actually looks for one. The facts that a ranking would
// have used (rate, free hours, already planned) are all still on each row, so
// the planner does the choosing with them in view.
export function MachinePickerDialog({
    productId,
    productDescription,
    groupWeight,
    candidates,
    onPick,
    onClose,
}: IMachinePickerDialog) {
    const actionable = candidates.filter((candidate) => candidate.selectable);
    // Rates arrive one query per machine when the dialog opens, so the usual
    // reason nothing is pickable for a second is simply that.
    const waitingForRates =
        actionable.length === 0 &&
        candidates.some((candidate) => candidate.rateStatus === 'pending');
    // A free machine of the right type with no computable rate at all — not even
    // its own average. The click is refused because the hours would be a guess —
    // but the product can still be planned by hand, and that is the sentence to
    // show.
    const blockedByHistory =
        actionable.length === 0 &&
        !waitingForRates &&
        candidates.some(
            (candidate) =>
                candidate.compatible &&
                candidate.hasFreeHours &&
                candidate.rateStatus === 'none',
        );

    return (
        <Dialog
            open={productId !== null}
            onClose={onClose}
            maxWidth={'xs'}
            fullWidth
        >
            <DialogTitle sx={{ pb: 0.5 }}>
                <Typography variant={'subtitle1'}>¿En qué máquina?</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {productDescription}
                </Typography>
                {/* What picking actually does, said once. The hours are not a
                    number the planner typed, so where they come from should not
                    have to be inferred. */}
                <Typography variant={'caption'} color={'text.secondary'}>
                    Se asignarán las horas necesarias para lo que falta de este
                    producto, sin pasarse de las horas libres del renglón.
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 1, pb: 2 }}>
                {actionable.length === 0 ? (
                    // The dialog opens even with nothing to pick, precisely so
                    // this sentence has somewhere to appear. A click that does
                    // nothing and explains nothing is the worst outcome.
                    <Alert severity={'info'} sx={{ mx: 1, mb: 1 }}>
                        {candidates.length === 0
                            ? 'La planeación no tiene ningún renglón con máquina.'
                            : waitingForRates
                            ? 'Calculando el rendimiento de este producto en cada máquina…'
                            : blockedByHistory
                            ? 'Ninguna de estas máquinas tiene rendimiento calculable, ni de este producto ni suyo propio, así que no se pueden calcular las horas desde aquí. Agrégalo en el renglón y escribe las horas tú.'
                            : 'Ningún renglón puede tomar este producto ahora mismo.'}
                    </Alert>
                ) : null}
                <List dense disablePadding>
                    {candidates.map((candidate, position) => {
                        const reason = reasonFor(candidate);
                        const header = headerFor(
                            candidate,
                            candidates[position - 1],
                        );
                        // The unit conversion is safe on any candidate: it
                        // divides by THIS product's kg-per-bulto, so it only
                        // re-expresses one number, never averages bultos across
                        // a machine's product mix. What it cannot do is make a
                        // borrowed rate any less borrowed — hence the suffix.
                        const rateLabel =
                            candidate.rate !== null
                                ? groupWeight && groupWeight > 0
                                    ? `${formatFloat(
                                          candidate.rate / groupWeight,
                                          1,
                                      )} bultos/h`
                                    : `${formatFloat(
                                          candidate.rate,
                                          0,
                                      )} kg/hr`
                                : null;
                        // A machine average is biased for this product, so a
                        // number derived from one must never read like a number
                        // measured on it. Says WHICH of the two cases it is,
                        // because "never run here" and "run four times" call for
                        // different amounts of scepticism.
                        const rateOrigin =
                            candidate.rate !== null &&
                            candidate.rateSource === 'machine'
                                ? candidate.productRunCount > 0
                                    ? `promedio de la máquina · sólo ${candidate.productRunCount} corrida${
                                          candidate.productRunCount === 1
                                              ? ''
                                              : 's'
                                      } de este producto aquí`
                                    : 'promedio de la máquina · sin historial de este producto aquí'
                                : null;
                        return (
                            <React.Fragment key={candidate.index}>
                                {header ? (
                                    <ListSubheader
                                        disableSticky
                                        sx={{
                                            lineHeight: 2,
                                            bgcolor: 'transparent',
                                        }}
                                    >
                                        {header}
                                    </ListSubheader>
                                ) : null}
                            <ListItemButton
                                disabled={!candidate.selectable}
                                onClick={() => onPick(candidate.index)}
                            >
                                <ListItemText
                                    primary={candidate.machineName}
                                    secondary={[
                                        reason,
                                        // The rate is shown only once it is
                                        // real: a machine average standing in
                                        // while the pair query is in flight
                                        // would be a different number in a
                                        // second, and this list is ranked by it.
                                        rateLabel,
                                        rateOrigin,
                                        candidate.hasFreeHours
                                            ? `${formatFloat(
                                                  candidate.freeHours,
                                                  1,
                                              )} h libres`
                                            : null,
                                    ]
                                        .filter(
                                            (part): part is string =>
                                                part !== null,
                                        )
                                        .join(' · ')}
                                />
                                {candidate.alreadyHas ? (
                                    // Kept as a chip, unlike the history, because
                                    // it is the exception rather than a property
                                    // every row has: this row is ALREADY making
                                    // the product, so picking it tops that line
                                    // up instead of opening another machine.
                                    <Chip
                                        size={'small'}
                                        variant={'outlined'}
                                        color={'primary'}
                                        label={`ya planeado · ${formatFloat(
                                            candidate.assignedHours,
                                            1,
                                        )} h`}
                                    />
                                ) : null}
                            </ListItemButton>
                            </React.Fragment>
                        );
                    })}
                </List>
            </DialogContent>
        </Dialog>
    );
}
